import { newWorkPath } from '@/app/work-tabs/work-tab-context';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PackagePlus, Plus, Search, ShoppingCart } from 'lucide-react';
import { PURCHASE_ORDER_STATUSES, type PurchaseOrderStatus } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PermissionGuard, usePermissions } from '@/features/auth';
import { usePurchaseOrders } from '@/features/procurement/api/use-procurement';
import { PurchaseOrderStatusBadge } from '@/features/procurement/components/status-badges';
import { DataCard, DataCardList } from '@/components/common/data-card';
import { formatDate, formatMoney } from '@/lib/formatters';
import { cn } from '@/lib/utils';

export function PurchaseOrdersRoute(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { canAccess } = usePermissions();
  const canWrite = canAccess('PROCUREMENT', 'UPDATE');

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statuses, setStatuses] = useState<PurchaseOrderStatus[]>([]);

  const filters = useMemo(
    () => ({ page, q: search, status: statuses, supplierId: null }),
    [page, search, statuses],
  );
  const orders = usePurchaseOrders(filters);

  function toggleStatus(status: PurchaseOrderStatus): void {
    setPage(1);
    setStatuses((current) =>
      current.includes(status) ? current.filter((entry) => entry !== status) : [...current, status],
    );
  }

  return (
    <PermissionGuard resource="PROCUREMENT" action="READ">
      <div className="space-y-6">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {t('procurement.orders.title')}
            </h1>
            <p className="text-sm text-muted-foreground">{t('procurement.orders.subtitle')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => void navigate('/procurement/goods-receipts/new')}
              disabled={!canWrite}
            >
              <PackagePlus className="mr-1.5 h-4 w-4" aria-hidden />
              {t('procurement.orders.newReceipt')}
            </Button>
            <Button
              type="button"
              onClick={() => void navigate(newWorkPath('/procurement/orders/new'))}
              disabled={!canWrite}
            >
              <Plus className="mr-1.5 h-4 w-4" aria-hidden />
              {t('procurement.orders.create')}
            </Button>
          </div>
        </header>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full sm:w-72">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              className="pl-8"
              placeholder={t('procurement.orders.searchPlaceholder')}
              aria-label={t('procurement.orders.searchPlaceholder')}
              value={search}
              onChange={(event) => {
                setPage(1);
                setSearch(event.target.value);
              }}
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {PURCHASE_ORDER_STATUSES.map((status) => (
              <button
                key={status}
                type="button"
                aria-pressed={statuses.includes(status)}
                onClick={() => toggleStatus(status)}
                className={cn(
                  'rounded-full border px-3 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  statuses.includes(status)
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-border text-muted-foreground hover:border-primary/50',
                )}
              >
                {t(`procurement.orderStatus.${status}`)}
              </button>
            ))}
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t('procurement.orders.cardTitle')}</CardTitle>
            <CardDescription>{t('procurement.orders.cardDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            {orders.isPending ? (
              <div className="space-y-2">
                <Skeleton className="h-9 w-full" />
                <Skeleton className="h-9 w-full" />
                <Skeleton className="h-9 w-full" />
              </div>
            ) : orders.isError ? (
              <div className="flex flex-col items-start gap-3">
                <p role="alert" className="text-sm text-destructive">
                  {t('procurement.orders.error')}
                </p>
                <Button type="button" variant="outline" onClick={() => void orders.refetch()}>
                  {t('common.retry')}
                </Button>
              </div>
            ) : orders.data.data.length === 0 ? (
              <div className="flex flex-col items-center gap-3 py-10 text-center">
                <ShoppingCart className="h-6 w-6 text-muted-foreground" aria-hidden />
                <p className="text-sm font-medium">{t('procurement.orders.emptyTitle')}</p>
                <p className="text-sm text-muted-foreground">{t('procurement.orders.emptyHint')}</p>
                <Button
                  type="button"
                  disabled={!canWrite}
                  onClick={() => void navigate(newWorkPath('/procurement/orders/new'))}
                >
                  <Plus className="mr-1.5 h-4 w-4" aria-hidden />
                  {t('procurement.orders.create')}
                </Button>
              </div>
            ) : (
              <>
                <DataCardList>
                  {orders.data.data.map((order) => (
                    <DataCard
                      key={order.id}
                      title={order.purchaseOrderNumber}
                      subtitle={order.supplierName}
                      badge={<PurchaseOrderStatusBadge status={order.status} />}
                      metric={formatMoney(order.grossTotal, order.currency, i18n.language)}
                      expandLabel={t('common.showDetails')}
                      collapseLabel={t('common.hideDetails')}
                      onOpen={() => void navigate(`/procurement/orders/${order.id}`)}
                      details={[
                        {
                          label: t('procurement.orders.columns.orderDate'),
                          value: formatDate(order.orderDate, i18n.language),
                        },
                        {
                          label: t('procurement.orders.columns.delivery'),
                          value: formatDate(order.expectedDeliveryDate, i18n.language),
                        },
                      ]}
                    />
                  ))}
                </DataCardList>

                <div className="hidden md:block">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('procurement.orders.columns.number')}</TableHead>
                        <TableHead>{t('procurement.orders.columns.supplier')}</TableHead>
                        <TableHead>{t('procurement.orders.columns.orderDate')}</TableHead>
                        <TableHead>{t('procurement.orders.columns.delivery')}</TableHead>
                        <TableHead>{t('procurement.orders.columns.status')}</TableHead>
                        <TableHead className="text-right">
                          {t('procurement.orders.columns.total')}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {orders.data.data.map((order) => (
                        <TableRow
                          key={order.id}
                          tabIndex={0}
                          className="cursor-pointer"
                          onClick={() => void navigate(`/procurement/orders/${order.id}`)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              void navigate(`/procurement/orders/${order.id}`);
                            }
                          }}
                        >
                          <TableCell className="font-medium">{order.purchaseOrderNumber}</TableCell>
                          <TableCell>{order.supplierName}</TableCell>
                          <TableCell>{formatDate(order.orderDate, i18n.language)}</TableCell>
                          <TableCell>
                            {formatDate(order.expectedDeliveryDate, i18n.language)}
                          </TableCell>
                          <TableCell>
                            <PurchaseOrderStatusBadge status={order.status} />
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatMoney(order.grossTotal, order.currency, i18n.language)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <nav
                  aria-label={t('common.pagination')}
                  className="mt-4 flex items-center justify-between"
                >
                  <p className="text-sm text-muted-foreground">
                    {t('common.paginationInfo', {
                      page: orders.data.meta.page,
                      totalPages: Math.max(orders.data.meta.totalPages, 1),
                      total: orders.data.meta.total,
                    })}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => setPage((current) => Math.max(current - 1, 1))}
                    >
                      {t('common.previous')}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={page >= orders.data.meta.totalPages}
                      onClick={() => setPage((current) => current + 1)}
                    >
                      {t('common.next')}
                    </Button>
                  </div>
                </nav>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </PermissionGuard>
  );
}
